import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import Captcha from "units/auth/components/turnstile";
import axiosInstance from "api/axiosInstance";
import { useNavigate } from "react-router-dom";
import SuccessModalFancy from "./SuccessModalFancy";
// ✅ اعتبارسنجی حرفه‌ای
const schema = yup.object().shape({
    name: yup
        .string()
        .matches(/^[\u0600-\u06FFA-Za-z0-9\s]+$/, "فقط حروف فارسی، انگلیسی، فاصله یا عدد مجاز است.")
        .required("نام الزامی است."),
    phone: yup
        .string()
        .matches(/^09\d{9}$/, "شماره موبایل باید با 09 شروع شود و 11 رقم باشد.")
        .required("شماره موبایل الزامی است."),
    email: yup.string().email("ایمیل وارد شده معتبر نیست.").required("ایمیل الزامی است."),
});

export default function FreeConsultationForm({ open, setOpen }) {
    const [captchaToken, setCaptchaToken] = useState("");
    const [isDemoLoading, setIsDemoLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const navigate = useNavigate();
    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
    });

    // ✅ ارسال فرم
    const onSubmit = async (data) => {
        if (!captchaToken) {
            toast.error("لطفاً کپچا را تأیید کنید.");
            return;
        }

        try {
            setIsDemoLoading(true);
            const result = await axiosInstance.post("/public/requestForDemo", {
                name: data.name,
                email: data.email,
                phone: data.phone,
                captchaToken,
            });

            if (result.data.ok) {
                toast.success("درخواست شما با موفقیت ثبت شد.");
                setSuccessMessage("✅ کارشناسان ما به‌زودی با شما تماس خواهند گرفت.");
                setSuccessModalOpen(true);
                reset();
                setCaptchaToken("");
            } else {
                toast.error(result.data.error || "خطایی رخ داده است.");
            }
        } catch (error) {
            console.error("Error submitting demo request:", error);
            const errorMessage =
                error.response?.data?.error ||
                error.message ||
                "خطا در ارسال درخواست دمو.";
            toast.error(errorMessage);
        } finally {
            setIsDemoLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => setOpen(false)} className="relative z-50">
            <DialogBackdrop className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out" />
            <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 flex justify-start">
                    <DialogPanel className="relative w-full max-w-md transform transition duration-500 ease-in-out sm:duration-700 bg-white shadow-xl rounded-l-2xl p-6">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="absolute top-4 left-4 text-gray-500 hover:text-gray-700"
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>

                        <DialogTitle as="h2" className="text-2xl font-bold text-center mb-6">
                            مشاوره و طرح <span className="text-yellow-500"> رایگان</span>
                        </DialogTitle>

                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="flex flex-col gap-4 justify-center items-center w-full"
                        >
                            {/* نام */}
                            <div className="w-full">
                                <input
                                    type="text"
                                    placeholder="نام و نام خانوادگی"
                                    {...register("name")}
                                    className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition ${errors.name
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-green-900"
                                        }`}
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                                )}
                            </div>

                            {/* تلفن */}
                            <div className="w-full">
                                <input
                                    type="tel"
                                    placeholder="شماره تلفن"
                                    {...register("phone")}
                                    className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition text-right ${errors.phone
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-green-900"
                                        }`}
                                />
                                {errors.phone && (
                                    <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                                )}
                            </div>

                            {/* ایمیل */}
                            <div className="w-full">
                                <input
                                    type="email"
                                    placeholder="ایمیل"
                                    {...register("email")}
                                    className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 transition ${errors.email
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-green-900"
                                        }`}
                                />
                                {errors.email && (
                                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                                )}
                            </div>

                            {/* کپچا */}
                            <Captcha tokenSetter={setCaptchaToken} className="w-full max-w-md" />

                            {/* دکمه ارسال */}
                            <button
                                type="submit"
                                disabled={isDemoLoading}
                                className={`mt-2 w-full py-3 rounded-md font-semibold text-white transition-colors ${isDemoLoading
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-yellow-500 hover:bg-yellow-600"
                                    }`}
                            >
                                {isDemoLoading ? "در حال ارسال..." : "ارسال"}
                            </button>

                            {/* {successMessage && (
                                <p className="mt-4 text-green-600 font-medium text-center animate-fade-in">
                                    {successMessage}
                                </p>
                            )} */}
                        </form>
                    </DialogPanel>
                    {/* مودال حرفه‌ای */}
                    <SuccessModalFancy
                        isOpen={successModalOpen}
                        setIsOpen={setSuccessModalOpen}
                        onConnect={() => {
                            window.open("/rayabot", "_blank");
                            setSuccessModalOpen(false);
                            setOpen(false);
                        }}
                        onHome={() => {
                            navigate("/");
                            setSuccessModalOpen(false);
                            setOpen(false);
                        }}
                        onCloseForm={() => setOpen(false)}
                        // onCloseForm={() =>{ setOpen(false);
                        //     navigate("/");
                        // }} //   بسته شدن فرم 
                    />
                </div>
            </div>
        </Dialog>
    );
}
